import React from "react";

import {
    Button,
    Box,
    Chip,
    Link,
    Stack,
    Typography,
    ListItem,
    ListItemButton,
    List,
} from "@mui/joy";

import {useStytchMember} from "@stytch/react/b2b";

import useCreateConversationAndNavigate from "../hooks/useCreateConversationAndNavigate";

import {ApiConstructabilityIssue, ApiMessageTypes} from "../shared/interfaces";
import {getMessageForSpreadsheetArgs} from "../util/objectUtil";

import {Link as ReactRouterLink} from "react-router-dom";

import MessageInput from "./MessageInput";
import {FileUploadProps} from "./FileUpload";
import ConversationList from "./ConversationList";
import {useQuery} from "react-query";
import {PrimeQueryKeys} from "../shared/constants";
import {FileApi} from "../api/file";
import FileIcon from "./FileIcon";

import {TransitionGroup, CSSTransition} from "react-transition-group";
import "./Home.css";
import {WorkflowApi} from "../api/workflow";
import {ConflictLikelihood, ConflictLikelihoodChip} from "./workflow/ConflictLikelihoodChip";

const MARVIN_URL =
    "https://stmainprodcentralus.blob.core.windows.net/primeassets/marvin_standing.png?sp=r&st=2025-05-23T22:20:59Z&se=2999-05-24T06:20:59Z&sv=2024-11-04&sr=b&sig=fdmsQJNImfVFMSiJ0gmz3j6rtYqL5jyCIli5jihfOYE%3D"; // trufflehog:ignore

const styles = {
    topContainer: {
        flex: 1,
        position: "relative",
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        paddingTop: 16,

        backgroundImage: `url(${MARVIN_URL})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "bottom left",
        backgroundColor: "#fdfefe",
        backgroundSize: "15%",
    },
    sectionTitle: {
        fontWeight: "bold",
        color: "var(--joy-palette-neutral-700)",
    },
    askQuestionContainer: {width: "700px", paddingBottom: 8},
    fileContainer: {flexWrap: "wrap"},
    fileButton: {p: 2},
};

const SAMPLE_QUESTIONS = [
    "What is the primer requirement for structural steel?",
    "Show me the pipe spacing requirement in concrete?",
    "Is there a detail for housekeeping pads?",
    "What submittals are required for the interior storefront?",
    "Who are the acceptable manufacturers for doors?",
];

export default function Home() {
    document.title = "Primepoint";

    const [uploadedFiles, setUploadedFiles] = React.useState(new Array<FileUploadProps>());

    const createConversation = useCreateConversationAndNavigate();
    const {member} = useStytchMember();
    const name = member?.name.split(" ")[0] ?? "User";

    const {data: fileData} = useQuery(PrimeQueryKeys.allFiles, FileApi.getAllFiles);
    const files = fileData ?? [];

    const getRandomQuestion = () => {
        const i = Math.floor(Math.random() * SAMPLE_QUESTIONS.length);
        return SAMPLE_QUESTIONS[i];
    };

    const [currentQuestion, setCurrentQuestion] = React.useState<string>(getRandomQuestion);

    React.useEffect(() => {
        const id = setInterval(() => {
            const random = getRandomQuestion();
            setCurrentQuestion(random);
        }, 5000);
        return () => clearInterval(id);
    }, []);

    const {data: issues = [], isLoading} = useQuery<ApiConstructabilityIssue[], Error>(
        PrimeQueryKeys.workflowProblems,
        WorkflowApi.getConstructabilityIssues,
        {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    );
    const issuesToShow = React.useMemo(() => issues.slice(0, 5), [issues]);

    return (
        <Stack direction="column" sx={styles.topContainer}>
            <Stack sx={styles.askQuestionContainer} spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                    <Typography level="title-lg">Howdy, {name}</Typography>
                    <Link
                        href="https://docs.google.com/document/d/e/2PACX-1vRhS4f4l7V9HHXKDVndVHszmdftRvH3pj_VKgCWrNsAgaErl5LI4BCeDqz6OOt_c6d6Z22L9K45EzIQ/pub"
                        level="body-xs"
                        target="_blank"
                    >
                        Check out the user guide
                    </Link>
                </Stack>
                <MessageInput
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    onSubmit={(msg) => createConversation(msg, ApiMessageTypes.Markdown)}
                    onSubmitGenerateSpreadsheet={(args) => {
                        createConversation(
                            getMessageForSpreadsheetArgs(args),
                            ApiMessageTypes.GenerateSpreadsheet,
                        );
                    }}
                />

                <Box sx={{height: 24, overflow: "hidden", position: "relative"}}>
                    <TransitionGroup component={null}>
                        <CSSTransition
                            key={currentQuestion}
                            timeout={400}
                            classNames="tryPrompt"
                            mountOnEnter
                            unmountOnExit
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{position: "absolute", width: "100%"}}
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Typography level="body-xs">Try</Typography>

                                <Chip
                                    onClick={() =>
                                        createConversation(
                                            currentQuestion,
                                            ApiMessageTypes.Markdown,
                                        )
                                    }
                                    size="sm"
                                    color="neutral"
                                    variant="soft"
                                >
                                    {currentQuestion}
                                </Chip>
                            </Stack>
                        </CSSTransition>
                    </TransitionGroup>
                </Box>

                <Stack spacing={4} sx={{pt: 8}}>
                    <Stack spacing={1}>
                        <Typography level="title-md" sx={styles.sectionTitle}>
                            Documents
                        </Typography>
                        <Stack direction="row" spacing={2} useFlexGap sx={styles.fileContainer}>
                            {files.map((f) => (
                                <Link
                                    key={f.id}
                                    component={ReactRouterLink}
                                    to={`/doc/${f.id}`}
                                    underline="none"
                                >
                                    <Button
                                        variant="outlined"
                                        size="lg"
                                        startDecorator={<FileIcon fileName={f.fileName}/>}
                                        color="neutral"
                                        sx={styles.fileButton}
                                    >
                                        {f.fileName}
                                    </Button>
                                </Link>
                            ))}
                        </Stack>
                    </Stack>

                    <Stack spacing={2}>
                        <Stack>
                            <Typography level="title-md" sx={styles.sectionTitle}>
                                Constructability issues
                            </Typography>
                            <Typography level="body-sm">
                                Marvin analyzed the specifications and drawings to find potential
                                conflicts.
                            </Typography>

                            {isLoading && (
                                <Typography level="body-xs" sx={{py: 1}}>
                                    Loading ...
                                </Typography>
                            )}
                        </Stack>

                        <List sx={{p: 0, m: 0}}>
                            {issuesToShow.map((issue) => (
                                <ListItem key={issue.issueNumber}>
                                    <ListItemButton component={ReactRouterLink} to={`/w/cr`}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            sx={{flex: 1, minWidth: 0}}
                                        >
                                            <Typography noWrap fontSize="sm">
                                                {issue.issueNumber}. {issue.title}
                                            </Typography>

                                            <ConflictLikelihoodChip
                                                sev={issue.severity as ConflictLikelihood}
                                            />
                                        </Stack>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>

                        {issues && (
                            <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                                <Button
                                    component={ReactRouterLink}
                                    to="/w/cr"
                                    size="sm"
                                    color="neutral"
                                    variant="plain"
                                >
                                    {isLoading ? "View all issues" : `View ${issues.length} issues`}
                                </Button>
                            </Box>
                        )}
                    </Stack>

                    <Stack spacing={1}>
                        <Typography level="title-md" sx={styles.sectionTitle}>
                            Previous questions
                        </Typography>
                        <ConversationList/>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
